import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { TeamContext, PlayerContext } from '../Context'
import { Box, MobileMenuContainer, PageContainerMain } from '../Components/Layout'
import { TeamTopLinkBar, TeamMobileLinkBar } from '../Components/TeamLinkBar'
import { SectionHeading, PageHeader } from '../Components/Headers'
import { CoachCardsGroup, SingleCoach } from '../Components/PersonStatsCard'
import { createTeamDynamicLink } from '../Utils'
import { PageContentLoading } from '../Components/Loading/PageContentLoading'
import { GeneralAppCallout } from '../Components/Layout/Overlays/AppCallouts/GeneralAppCallout'
import { CoachRow } from '../Components/PersonStatsCard/styles'
import { RegSeasonRoster } from './Team/components/rosters/regularSeason/RegSeasonRosters'
import { BasicAccordion, AccordionItem } from '../Components/Layout/Accordion'
import {
  ChampSeriesRoster,
  ChampSeriesRosterHeader,
  RegSeasonRosterHeader,
} from './Team'
import { Banner } from '../Api'
import { AdUnit } from '../Components/Ads/AdUnit'
import {
  CURRENT_YEAR,
  EVENTS_YEAR,
  hasChampSeriesStarted,
} from '../Config/League/league.config'
import { champSeriesTeams } from '../Constant/team'

const Roster: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { team, banners, getHeadCoach, getAssistantCoaches } = useContext(TeamContext)!
  const { isRosterLoading, roster, champSeriesRoster } = useContext(PlayerContext)!
  const [banner, setBanner] = useState<Banner>(null)
  const head = useCallback(() => getHeadCoach(), [team])

  useEffect(() => {
    if (banners.length > 0) {
      setBanner(banners[0])
    }
  }, [banners])

  const accordionItems: AccordionItem[] = useMemo(() => {
    return [
      {
        headerComponent: (
          <RegSeasonRosterHeader
            teamName={team?.fullName}
            rosterLength={roster.length}
          />
        ),
        summaryComponent: <RegSeasonRoster />,
      },
      {
        headerComponent: (
          <ChampSeriesRosterHeader rosterLength={champSeriesRoster.length} />
        ),
        summaryComponent: <ChampSeriesRoster />,
        colorTheme: 'gray',
      },
    ]
  }, [isRosterLoading, roster, champSeriesRoster])

  const shouldShowCSRoster = () => {
    if (
      hasChampSeriesFinished() ||
      !team?.officialId ||
      !champSeriesTeams[EVENTS_YEAR].includes(team.officialId.toLowerCase())
    )
      return false

    return hasChampSeriesStarted
  }

  const hasChampSeriesFinished = () => {
    if (EVENTS_YEAR !== CURRENT_YEAR) return false
    if (team?.champSeries?.events && team.champSeries.events.length > 0) {
      //Get last event to check if ended
      return team.champSeries.events[team.champSeries.events.length - 1].eventStatus > 1
    }
    return false
  }

  return (
    <>
      {team && (
        <GeneralAppCallout
          text={`Get ${team.fullName} roster updates in the PLL App`}
          appLink={createTeamDynamicLink(team, 'roster')}
          location={`${team.fullName}_Roster_Page`}
        />
      )}
      <PageContainerMain useFooter>
        <AdUnit bannerType="bannerMain" aria-label="Roster page ad" />
        <Box>
          <TeamTopLinkBar shop stats hideOnMobile />
        </Box>
        {team && (
          <>
            <PageHeader
              banner={banner}
              img={team.teamBanner}
              alt={`${team.fullName} banner`}
            />
          </>
        )}

        {isRosterLoading && <PageContentLoading />}
        {shouldShowCSRoster() && ( //Check if team is participating
          <BasicAccordion
            defaultPanel={!hasChampSeriesFinished() ? 2 : 1}
            accordionItems={accordionItems}
          />
        )}
        {!shouldShowCSRoster() && roster.length > 0 && <RegSeasonRoster />}

        {team?.coaches && (
          <Box>
            <CoachRow>
              <div className="coachesCol">
                <SectionHeading headingText="Head coach" />
                <SingleCoach coach={head()} />
              </div>
              <div className="coachesCol">
                <SectionHeading headingText="Assistant Coaches" />
                <CoachCardsGroup coaches={getAssistantCoaches()} />
              </div>
            </CoachRow>
          </Box>
        )}
      </PageContainerMain>
      <MobileMenuContainer>
        <TeamMobileLinkBar shop stats />
      </MobileMenuContainer>
    </>
  )
}

export default Roster
